import { FolderLock, Mail } from "lucide-react";
import { requestAccessHref, useAccessManagers } from "./request-access";

function projectAccessHref(email: string, organizationName: string | null) {
  return requestAccessHref(
    email,
    "Project access request",
    `Hi, could you add me to the projects I should work on${organizationName ? ` in ${organizationName}` : ""}?`
  );
}

/**
 * Empty state for members and reviewers who can't create projects and haven't been assigned one.
 * It names the people who can grant access instead of offering an action the server would refuse.
 */
export function WaitingForProjectsState({
  isReviewer,
  organizationName,
}: {
  isReviewer: boolean;
  organizationName: string | null;
}) {
  const managers = useAccessManagers();

  return (
    <section
      aria-labelledby="waiting-for-projects-title"
      className="flex flex-col items-center rounded-lg border-2 border-dashed border-border bg-card/50 p-6 text-center sm:p-10"
    >
      <div className="mx-auto flex size-16 items-center justify-center rounded-xl border-2 border-muted bg-muted/50">
        <FolderLock className="size-8 text-muted-foreground" aria-hidden="true" />
      </div>
      <h2 id="waiting-for-projects-title" className="mt-5 text-lg font-bold text-foreground">
        You haven't been added to a project yet
      </h2>
      <p className="mt-2 max-w-md text-sm leading-relaxed text-muted-foreground">
        {isReviewer
          ? "As a reviewer you can open the projects you're assigned to and download completed submittals. An admin adds you to projects."
          : "Projects you're added to appear here, ready to upload schedules and run SpecPilot. An admin adds you to projects."}
      </p>
      {managers.length > 0 && (
        <div className="mt-6 w-full max-w-md space-y-2 text-start">
          <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Ask an admin for access</h3>
          <ul className="space-y-2">
            {managers.map((manager) => (
              <li
                key={manager._id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-md border-2 border-border bg-card px-3 py-2"
              >
                <span className="min-w-0 truncate text-sm font-medium" title={manager.email}>
                  {manager.name}
                </span>
                <a
                  href={projectAccessHref(manager.email, organizationName)}
                  className="inline-flex min-h-9 items-center gap-1.5 rounded-md px-2 text-sm font-semibold text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  aria-label={`Email ${manager.name} to request project access`}
                >
                  <Mail className="size-4" aria-hidden="true" />
                  Request access
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
